import React, { useState, useEffect, useMemo } from 'react';
import {

  Button,
  Box,
  Typography,
  Checkbox,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  Alert,
  CircularProgress,
} from '@mui/material';
import { XIcon, FilmSlateIcon, LockIcon, GlobeIcon, UsersThreeIcon, CaretRightIcon } from '@phosphor-icons/react';

import { useUserWatchlistData, type Watchlist } from '../../contexts/UserWatchlistDataContext';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import { getStandardizedMovieId } from '../../utils/movieUtils';
import { User } from 'firebase/auth';
import { glassStyles } from '../../tokens/designTokens';
import { tmdbService, TMDbMovie } from '../../services/tmdbApi';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import ImageShimmer from '../widgets/ImageShimmer';
import CompactMovieCard from '../widgets/CompactMovieCard';
import MovieDetail from '../pages/MovieDetail';
import { useCommonToasts } from '../../hooks/useCommonToasts';
import { font } from '../../utils/fontHelper';
import {
  getWatchlistLastActivityMs,
  watchlistInteractionRank,
} from '../../utils/watchlistRecency';
import { DEFAULT_WATCHLISTS } from '@watchlist/shared';

/** Higher = more recently used; combines list + movie activity with client recency (adds/notes). */
function watchlistRecencySortKey(w: Watchlist): number {
  return Math.max(
    getWatchlistLastActivityMs(w),
    watchlistInteractionRank(w.id, w.updatedAt)
  );
}

interface WatchlistMultiSelectDialogProps {
  open: boolean;
  onClose: () => void;
  movieId: string;
  movieTitle: string;
  moviePoster?: string;
  movieYear?: string;
  user?: User;
  onSave?: () => void;
  isWatched?: boolean;
  mode?: 'save' | 'select';
  initialSelection?: Set<string>;
  onSelect?: (selection: Set<string>) => void;
  onNavigate?: (watchlistId: string) => void;
  zIndex?: number;
}


const WatchlistMultiSelectDialog: React.FC<WatchlistMultiSelectDialogProps> = ({
  open,
  onClose,
  movieId,
  movieTitle,
  moviePoster,
  movieYear,
  user,
  onSave,
  isWatched,
  mode = 'save',
  initialSelection,
  onSelect,
  onNavigate,
  zIndex
}) => {

  const [selectedWatchlists, setSelectedWatchlists] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { userWatchlists, addMovieToWatchlist, removeMovieFromWatchlist } = useUserWatchlistData();
  const { addToWatchlist, removeFromWatchlist, getUserMovieData, refreshMovieData } = useUserMovieData();
  const toasts = useCommonToasts();

  // Exclude Firestore default id — the dedicated "Want to Watch" row uses personal-watchlist.
  // Sort: most recently used first. When the movie is watched, that row is hidden so this order
  // starts at the top; when unwatched, Want to Watch is rendered above this list.
  const watchlistsSortedByRecentActivity = useMemo(() => {
    const customOnly = userWatchlists.filter((w) => w.id !== DEFAULT_WATCHLISTS.WANT_TO_WATCH);
    return [...customOnly].sort(
      (a, b) => watchlistRecencySortKey(b) - watchlistRecencySortKey(a)
    );
  }, [userWatchlists]);

  // Initialize selected watchlists based on current movie status or props
  useEffect(() => {
    if (open) {
      if (mode === 'select' && initialSelection) {
        setSelectedWatchlists(new Set(initialSelection));
        setError(null);
        return;
      }

      if (movieId) {
        const newSelected = new Set<string>();

        // Check if movie is in personal watchlist
        const movieData = getUserMovieData(movieId);

        // 🚀 NEW LOGIC: Only add to personal watchlist if not watched
        // Since watched movies are automatically removed from want-to-watch
        if (movieData?.isInWatchlist && !movieData?.isWatched) {
          newSelected.add('personal-watchlist');
        }

        // Check which user watchlists contain this movie
        userWatchlists.forEach(watchlist => {
          const hasMovie = watchlist.movieDetails.some(movie => {
            const watchlistMovieId = getStandardizedMovieId(movie);
            const targetMovieId = getStandardizedMovieId({ id: movieId, imdbID: movieId });
            return watchlistMovieId === targetMovieId;
          });
          if (hasMovie) {
            newSelected.add(watchlist.id);
          }
        });

        // Default selection for add flows: Want to Watch when movie is not in any list yet (user can change)
        if (mode === 'save' && !movieData?.isWatched && newSelected.size === 0) {
          newSelected.add('personal-watchlist');
        }

        console.log('WatchlistMultiSelectDialog: Initialized selected watchlists:', {
          movieId,
          selectedWatchlists: Array.from(newSelected),
          personalWatchlistStatus: movieData?.isInWatchlist,
          isWatched: movieData?.isWatched,
          note: 'Watched movies automatically removed from want-to-watch'
        });

        setSelectedWatchlists(newSelected);
        setError(null);
      }
    }
  }, [open, movieId, getUserMovieData, userWatchlists, mode, initialSelection]);


  const handleWatchlistToggle = (watchlistId: string) => {
    const newSelected = new Set(selectedWatchlists);

    if (watchlistId === 'personal-watchlist') {
      // Toggle personal watchlist (want-to-watch)
      if (newSelected.has(watchlistId)) {
        newSelected.delete(watchlistId);
      } else {
        newSelected.add(watchlistId);
      }
    } else {
      // Custom watchlist toggle
      if (newSelected.has(watchlistId)) {
        newSelected.delete(watchlistId);
      } else {
        newSelected.add(watchlistId);
      }
    }

    setSelectedWatchlists(newSelected);
    setError(null);
  };

  const handleSave = async () => {
    // In Isolate/Select mode, just return the selection
    if (mode === 'select') {
      if (onSelect) {
        onSelect(selectedWatchlists);
      }
      onClose();
      return;
    }

    if (!movieId || !movieTitle) {
      setError('Missing movie information');
      return;
    }


    setSaving(true);
    setError(null);

    try {
      console.log(`🎬 WatchlistMultiSelectDialog: Saving movie "${movieTitle}" (${movieId})`);
      console.log('Selected watchlists:', Array.from(selectedWatchlists));

      const movieData = getUserMovieData(movieId);
      const isCurrentlyInPersonalWatchlist = movieData?.isInWatchlist || false;
      const shouldBeInPersonalWatchlist = selectedWatchlists.has('personal-watchlist');

      // Handle personal watchlist (Want to Watch)
      // 🚀 NEW LOGIC: Don't allow adding watched movies to want-to-watch
      const isWatched = movieData?.isWatched;

      if (shouldBeInPersonalWatchlist && !isCurrentlyInPersonalWatchlist) {
        if (isWatched) {
          console.log('⚠️ Cannot add watched movie to want-to-watch list');
          // Show toast instead of error state
          toasts.permissionError();
          return;
        }
        console.log('✅ Adding to personal watchlist');
        await addToWatchlist(movieId, movieTitle, moviePoster, String(movieYear || 'Unknown'));
      } else if (!shouldBeInPersonalWatchlist && isCurrentlyInPersonalWatchlist) {
        console.log('❌ Removing from personal watchlist');
        await removeFromWatchlist(movieId);
      }

      // Handle custom watchlists
      for (const watchlist of watchlistsSortedByRecentActivity) {
        const hasMovie = watchlist.movieDetails.some(movie => {
          const watchlistMovieId = getStandardizedMovieId(movie);
          const targetMovieId = getStandardizedMovieId({ id: movieId, imdbID: movieId });
          return watchlistMovieId === targetMovieId;
        });
        const shouldHaveMovie = selectedWatchlists.has(watchlist.id);

        if (shouldHaveMovie && !hasMovie) {
          // Add to watchlist
          console.log(`✅ Adding to custom watchlist "${watchlist.name}"`);
          const movieToAdd = {
            id: movieId,
            title: movieTitle,
            year: String(movieYear || 'Unknown'),
            poster: moviePoster,
            imdbID: movieId,
          };

          await addMovieToWatchlist(watchlist.id, movieToAdd);

          // Refresh movie data to ensure cache is updated
          await refreshMovieData(movieId);

        } else if (!shouldHaveMovie && hasMovie) {
          // Remove from watchlist
          console.log(`❌ Removing from custom watchlist "${watchlist.name}"`);
          await removeMovieFromWatchlist(watchlist.id, movieId);
        }
      }

      console.log('🎉 All watchlist operations completed successfully');

      if (onSave) {
        onSave();
      }

      onClose();
    } catch (error) {
      console.error('❌ Error updating watchlists:', error);
      setError(`Failed to update watchlists: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const getWatchlistIcon = (watchlist: any) => {
    if (watchlist.isPrivate) {
      return <LockIcon size={16} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'} />;
    } else if (watchlist.collaborators && watchlist.collaborators.length > 0) {
      return <UsersThreeIcon size={16} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'} />;
    } else {
      return <GlobeIcon size={16} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'} />;
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError(null);
      onClose();
    }
  };

  // Reset error when dialog closes
  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  if (isMobile) {
    // Mobile Bottom Sheet
    return (
      <BottomSheet
        open={open}
        onClose={handleClose}
        height="80vh"
        zIndex={zIndex || 1400}
      >
        {/* Header with close button */}
        <BottomSheetDraggableHeader sx={{
          position: 'relative',
          p: 3,
          flexShrink: 0,
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontFamily: font(),
            color: theme.palette.text.primary,
            fontSize: 18,
          }}>
            Add to watchlist
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={saving}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 40,
              height: 40,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }
            }}
          >
            <XIcon size={20} color={theme.palette.text.primary} />
          </IconButton>
        </BottomSheetDraggableHeader>

        {/* Error Alert */}
        {error && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Content - Scrollable */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          pt: 0,
          pb: 20,
          transition: 'padding-bottom 0.3s ease',
        }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Movie Card */}
            <Box sx={{ mb: 2 }}>
              <CompactMovieCard
                poster={moviePoster && moviePoster !== 'N/A' ? tmdbService.getHighQualityPoster(moviePoster) : null}
                title={movieTitle}
                year={movieYear}
              />
            </Box>

            {/* Personal Watchlist (Want to Watch) — first list row; hide for watched movies */}
            {!isWatched && (
              <Box sx={{
                p: 1.5,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                borderRadius: 3,
                border: `1px solid ${theme.palette.mode === 'dark' ? '#3A3A3C' : 'rgba(0,0,0,0.08)'}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(88, 86, 214, 0.2)' : 'rgba(88, 86, 214, 0.08)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
                onClick={() => handleWatchlistToggle('personal-watchlist')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Checkbox
                    checked={selectedWatchlists.has('personal-watchlist')}
                    onChange={() => handleWatchlistToggle('personal-watchlist')}
                    disabled={saving}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#8B88FF' : '#5856D6',
                      '&.Mui-checked': {
                        color: theme.palette.mode === 'dark' ? '#f8f8fce7' : '#5856D6',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      }
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{
                      fontWeight: 500,
                      fontFamily: font(),
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                      fontSize: 14,
                      lineHeight: 1.2,
                    }}>
                      Want to Watch
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                      fontFamily: font(),
                      fontSize: 12,
                      fontWeight: 400,
                    }}>
                      Your personal watchlist
                    </Typography>
                  </Box>

                  {/* Navigation Arrow */}
                  {(() => {
                    const movieData = getUserMovieData(movieId);
                    const isInWantToWatch = movieData?.isInWatchlist && !movieData?.isWatched;

                    if (isInWantToWatch && onNavigate) {
                      return (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate('want-to-watch');
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                              color: theme.palette.text.primary,
                            }
                          }}
                        >
                          <CaretRightIcon size={14} />
                        </IconButton>
                      );
                    }
                    return null;
                  })()}
                </Box>
              </Box>
            )}

            {/* User's Custom Watchlists */}
            {watchlistsSortedByRecentActivity.length > 0 ? (
              watchlistsSortedByRecentActivity.map((watchlist) => (
                <Box
                  key={watchlist.id}
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#3A3A3C' : 'rgba(0,0,0,0.08)'}`,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(118, 118, 128, 0.12)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                  }}
                  onClick={(e) => {
                    // If clicking the container and not the specific arrow, just toggle
                    handleWatchlistToggle(watchlist.id);
                  }}
                >

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Checkbox
                      checked={selectedWatchlists.has(watchlist.id)}
                      onChange={() => handleWatchlistToggle(watchlist.id)}
                      disabled={saving}
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#7D7AFF' : '#3634A3',
                        '&.Mui-checked': {
                          color: theme.palette.mode === 'dark' ? '#f8f8fce7' : '#5856D6',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 24,
                        }
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography variant="body1" sx={{
                          fontWeight: 500,
                          fontFamily: font(),
                          color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                          fontSize: 14,
                          lineHeight: 1.2,
                        }}>
                          {watchlist.name}
                        </Typography>
                        {getWatchlistIcon(watchlist)}
                      </Box>
                      <Typography variant="caption" sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                        fontFamily: font(),
                        fontSize: 12,
                        fontWeight: 400,
                      }}>
                        {watchlist.movieDetails.length} movies
                      </Typography>
                    </Box>

                    {/* Navigation Arrow */}
                    {(() => {
                      const hasMovie = watchlist.movieDetails.some(movie => {
                        const watchlistMovieId = getStandardizedMovieId(movie);
                        const targetMovieId = getStandardizedMovieId({ id: movieId, imdbID: movieId });
                        return watchlistMovieId === targetMovieId;
                      });

                      if (hasMovie && onNavigate) {
                        return (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigate(watchlist.id);
                            }}
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                color: theme.palette.text.primary,
                              }
                            }}
                          >
                            <CaretRightIcon size={14} />
                          </IconButton>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{
                textAlign: 'center',
                py: 6,
                color: theme.palette.text.secondary,
                flexShrink: 0,
              }}>
                <Typography variant="body2" sx={{
                  fontFamily: font(),
                  fontSize: 14,
                  lineHeight: 1.5,
                }}>
                  No custom watchlists found.<br />Create a custom watchlist to add movies to it.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Fixed Bottom Actions */}
        <Box sx={{
          p: 3, display: 'flex',
          gap: 3,
          justifyContent: 'center',
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}>
          <CustomButton
            onClick={handleClose}
            variant="contained"
            disabled={saving}
            semantic="tertiary"
            sx={{ minWidth: 120, flex: 1 }}
          >
            Cancel
          </CustomButton>

          <CustomButton
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            semantic="brand"
            sx={{ minWidth: 120, flex: 1 }}
          >

            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                Saving...
              </Box>
            ) : (
              mode === 'select' ? 'Done' : 'Save'
            )}

          </CustomButton>
        </Box>
      </BottomSheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        }
      }}
    >
      {/* Header with close button */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          fontFamily: font(),
          color: theme.palette.text.primary,
          fontSize: 18,
        }}>
          Add to watchlist
        </Typography>
      </Box>

      <IconButton
        onClick={handleClose}
        disabled={saving}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 40,
          height: 40,
          bgcolor: 'transparent',
          zIndex: 1,
          '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }
        }}
      >
        <XIcon size={20} color={theme.palette.text.primary} />
      </IconButton>

      {/* Error Alert */}
      {error && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Content - Scrollable */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        pb: 3,
        maxHeight: 'calc(80vh - 140px)',
      }}>
        {/* Movie Card */}
        <Box sx={{ mb: 3 }}>
          <CompactMovieCard
            poster={moviePoster && moviePoster !== 'N/A' ? tmdbService.getHighQualityPoster(moviePoster) : null}
            title={movieTitle}
            year={movieYear}
          />
        </Box>

        {/* Scrollable watchlist content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Personal Watchlist (Want to Watch) - Hide for watched movies */}
          {!isWatched && (
            <Box sx={{
              p: 1.5,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
              borderRadius: 3,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#3A3A3C' : 'rgba(0,0,0,0.08)'}`,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(88, 86, 214, 0.2)' : 'rgba(88, 86, 214, 0.08)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
              onClick={() => handleWatchlistToggle('personal-watchlist')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Checkbox
                  checked={selectedWatchlists.has('personal-watchlist')}
                  onChange={() => handleWatchlistToggle('personal-watchlist')}
                  disabled={saving}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#8B88FF' : '#5856D6',
                    '&.Mui-checked': {
                      color: theme.palette.mode === 'dark' ? '#f8f8fce7' : '#5856D6',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 24,
                    }
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{
                    fontWeight: 500,
                    fontFamily: font(),
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                    fontSize: 15,
                    mb: 0,
                  }}>
                    Want to Watch
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                    fontFamily: font(),
                    fontSize: 12,
                    fontWeight: 400,
                  }}>
                    Your personal watchlist
                  </Typography>
                </Box>

                {/* Navigation Arrow */}
                {(() => {
                  const movieData = getUserMovieData(movieId);
                  const isInWantToWatch = movieData?.isInWatchlist && !movieData?.isWatched;

                  if (isInWantToWatch && onNavigate) {
                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigate('want-to-watch');
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            color: theme.palette.text.primary,
                          }
                        }}
                      >
                        <CaretRightIcon size={14} />
                      </IconButton>
                    );
                  }
                  return null;
                })()}
              </Box>
            </Box>
          )}

          {/* User's Custom Watchlists */}
          {watchlistsSortedByRecentActivity.length > 0 ? (
            watchlistsSortedByRecentActivity.map((watchlist) => (
              <Box
                key={watchlist.id}
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#3A3A3C' : 'rgba(0,0,0,0.08)'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(118, 118, 128, 0.12)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
                onClick={(e) => {
                  handleWatchlistToggle(watchlist.id);
                }}
              >

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Checkbox
                    checked={selectedWatchlists.has(watchlist.id)}
                    onChange={() => handleWatchlistToggle(watchlist.id)}
                    disabled={saving}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#7D7AFF' : '#3634A3',
                      '&.Mui-checked': {
                        color: theme.palette.mode === 'dark' ? '#f8f8fce7' : '#3634A3',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      }
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Typography variant="body1" sx={{
                        fontWeight: 500,
                        fontFamily: font(),
                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                        fontSize: 15,
                      }}>
                        {watchlist.name}
                      </Typography>
                      {getWatchlistIcon(watchlist)}
                    </Box>
                    <Typography variant="caption" sx={{
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                      fontFamily: font(),
                      fontSize: 12,
                      fontWeight: 400,
                    }}>
                      {watchlist.movieDetails.length} movies
                    </Typography>
                  </Box>

                  {/* Navigation Arrow */}
                  {(() => {
                    const hasMovie = watchlist.movieDetails.some(movie => {
                      const watchlistMovieId = getStandardizedMovieId(movie);
                      const targetMovieId = getStandardizedMovieId({ id: movieId, imdbID: movieId });
                      return watchlistMovieId === targetMovieId;
                    });

                    if (hasMovie && onNavigate) {
                      return (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate(watchlist.id);
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                              color: theme.palette.text.primary,
                            }
                          }}
                        >
                          <CaretRightIcon size={14} />
                        </IconButton>
                      );
                    }
                    return null;
                  })()}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 6,
              color: theme.palette.text.secondary,
              flexShrink: 0,
            }}>
              <Typography variant="body2" sx={{
                fontFamily: font(),
                fontSize: 15,
                lineHeight: 1.5,
              }}>
                No custom watchlists found.<br />Create a custom watchlist to add movies to it.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          mt: 3,
        }}>
          <CustomButton
            onClick={handleClose}
            variant="contained"
            disabled={saving}
            semantic="tertiary"
            sx={{ minWidth: 120, flex: 1 }}
          >
            Cancel
          </CustomButton>

          <CustomButton
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            semantic="brand"
            sx={{ minWidth: 120, flex: 1 }}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                Saving...
              </Box>
            ) : (
              mode === 'select' ? 'Done' : 'Save'
            )}

          </CustomButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WatchlistMultiSelectDialog; 